// @ts-check
import {distanceBetween} from 'geofire-common';
import pencilIcon from '@/assets/pencil.png';
import {isMobile} from '@/lib/ReusableFunctions/User';
import * as mercator from '@/lib/mercator';

/// <reference types="google.maps" />
/** @typedef {google.maps.LatLngLiteral} LatLngLiteral */
/**
 * @typedef {Object} Point
 * @property {number} x x coordinate
 * @property {number} y y coordinate
 *
 * @typedef {Object} MapSettings
 * @property {number} zoom - The zoom level of the map.
 * @property {number} lat - The latitude coordinate of the map.
 * @property {number} lng - The longitude coordinate of the map.
 *
 * @typedef {'wait'|'crosshair'|'pencil'|'text'|'move'|'grab'|'pointer'|'default'} CursorOption
 *
 * @typedef {Object} TileResult
 * @property {HTMLImageElement} el The matched tile element.
 * @property {string} placement The placement of the tile relative to the clicked point.
 *
 * @typedef {Object} CustomClickEvent
 * @property {google.maps.LatLng} latLng - The latitude and longitude of the click event.
 * @property {Object} pixel - The pixel coordinates of the click event.
 * @property {number} pixel.x - The x coordinate of the click event.
 * @property {number} pixel.y - The y coordinate of the click event.
 *
 * @typedef {object} LatLngBoundsLiteralStrings
 * @property {string} north The latitude of the northern edge.
 * @property {string} east The longitude of the eastern edge.
 * @property {string} south The latitude of the northern edge.
 * @property {string} west The longitude of the western edge.
 */

const EARTH_RADIUS_KM = 6378.137;
const KM_TO_FEET = 3280.84;
/**
 * Convenience constant to provide access to the Google Maps API object.
 * The Google Maps API is linked in public/index.html.
 * The API automatically adds itself to the global scope when it is loaded.
 * @constant
 * @type {google.maps}
 */
const gmAPI = window.google.maps;
// with async loading enabled, we need to import the libraries we use.
// importing any specific library will automatically import the base map library as well.
await Promise.all([
  gmAPI.importLibrary('places'),
  gmAPI.importLibrary('geometry'),
  gmAPI.importLibrary('marker'),
]);
/** @type {string[]} list of sub-pane IDs to create */
const SUBPANE_IDS = ['overlay-graphics', 'layer-graphics'];

/** @type {Map<string, google.maps.MapsEventListener>} */
const _listeners = new Map(); // this is a data structure and the constructor "Map"
// has nothing to do with the Google Maps API
/** @type {google.maps.Map} */
let _map = null;
/** @type {HTMLElement} */
let _mapElement = null;
/** @type {Promise<HTMLDivElement[]>} created sub-panes */
let subPanes = null;
/** @type {google.maps.LatLngBounds} bounds representing the visible region covered by the Overlay canvas. */
let viewBounds = null;

const MapService = {
  /**
   * @param {number} x screen pixel x value
   * @param {number} y screen pixel y value
   * @param {boolean} returnLiteral If `true`, returns a `LatLngLiteral`.
   * @return {google.maps.LatLng | google.maps.LatLngLiteral} lat/lng of the screen pixel
   */
  screenPixelToLatLng: function(x, y, returnLiteral = false) {
    const projection = this.getMap().getProjection();
    if (!projection) {
      console.warn('Attempted to access map projection before it was ready.');
      return;
    }
    x = x - this.getMap().getDiv().offsetLeft;
    // world pixel between {0-256}, {0-256}. Screen pixels are worldPixel*2^zoom
    const top = this.getMap().getProjection().fromLatLngToPoint(this.getMap().getBounds().getNorthEast()).y;
    const left = this.getMap().getProjection().fromLatLngToPoint(this.getMap().getBounds().getSouthWest()).x;
    // screen pixels are {0-screenWidth}, {0-screenHeight}. Scaled to world coordinate space by screenPixel/2^zoom
    // screen pixels represent the difference from the top left corner of world coordinate space
    const scale = (Math.pow(2, this.getMap().getZoom()));
    const worldPoint = new gmAPI.Point(
      left + x / scale,
      top + y / scale,
    );
    const latLng = this.getMap().getProjection().fromPointToLatLng(worldPoint);
    if (returnLiteral) {
      // In some instances we need a serializable object, so we return a LatLngLiteral.
      // An example is Playwright's page.evaluate() method, which can't return non-serializable objects.
      return {lat: latLng.lat(), lng: latLng.lng()};
    }
    return latLng;
  },
  /**
   * @param {google.maps.LatLng|google.maps.LatLngLiteral} latLng lat/lng of point to convert to screen pixel
   * @return {Point} screen pixel x and y values
   */
  latLngToScreenPixel: function(latLng) {
    const projection = this.getMap().getProjection();
    if (!projection) {
      console.warn('Attempted to access map projection before it was ready.');
      return;
    }
    // world pixel between {0-256}, {0-256}. Screen pixels are worldPixel*2^zoom
    const top = this.getMap().getProjection().fromLatLngToPoint(this.getMap().getBounds().getNorthEast()).y;
    const left = this.getMap().getProjection().fromLatLngToPoint(this.getMap().getBounds().getSouthWest()).x;
    const scale = Math.pow(2, this.getMap().getZoom());
    const worldPoint = this.getMap().getProjection().fromLatLngToPoint(latLng);
    const screenPoint = {
      // The desktop menu pushes the map div to the left, so we need to adjust screen pixels accordingly.
      x: ((worldPoint.x - left) * scale) + this.getMap().getDiv().offsetLeft,
      y: ((worldPoint.y - top) * scale),
    };
    return screenPoint;
  },
  /**
   * initialize the map
   * @param {number} lat initial map center latitude
   * @param {number} lng initial map center longitude
   * @param {number} zoom initial map zoom level
   * @param {HTMLElement} mapElement DOM element to use for map
   */
  initializeMap: function(lat, lng, zoom, mapElement) {
    _map = new gmAPI.Map(mapElement, {
      cameraControl: false,
      center: new gmAPI.LatLng(lat, lng),
      clickableIcons: false, // determines whether points of interest are clickable
      disableDoubleClickZoom: true,
      fullscreenControl: !isMobile(),
      isFractionalZoomEnabled: true,
      keyboardShortcuts: false,
      styles: [{featureType: 'poi', stylers: [{visibility: 'off'}]}],
      mapTypeId: 'hybrid',
      mapTypeControl: true,
      mapTypeControlOptions: {
        position: gmAPI.ControlPosition.TOP_RIGHT,
      },
      rotateControl: false,
      scaleControl: true,
      streetViewControlOptions: {
        position: gmAPI.ControlPosition.RIGHT_TOP,
      },
      tiltInteractionEnabled: false,
      tilt: 0, // Determine the angle of the map
      zoom: zoom,
      zoomControl: true,
      zoomControlOptions: {
        position: gmAPI.ControlPosition.RIGHT_TOP,
      },
      gestureHandling: 'greedy',
    });
    _map.setStreetView(
      new gmAPI.StreetViewPanorama(
        mapElement,
        {
          visible: false,
          enableCloseButton: true,
          addressControlOptions: {
            position: ('maxTouchPoints' in navigator && navigator.maxTouchPoints > 0) ? gmAPI.ControlPosition.TOP_CENTER : gmAPI.ControlPosition.BOTTOM_CENTER,
          },
        },
      ),
    );
    _mapElement = mapElement;
    // for backwards compatibility, add the map to the global namespace
    // @ts-ignore this contaminates the global namespace, but it's very useful for debugging
    window.map = _map;
    subPanes = null; /* subPanes must be set to null when initializing shareable maps, otherwise the 'overlay-graphic'
                        div will not be inserted into the DOM when navigating to maps via the vue router and images
                        will not render */
  },
  /**
   * Adds a listener to the map for the given event and tracks the listener for deletion later
   * @param {string?} listenerID ID of the listener for later deletion
   * @param {string} eventName name of event to listen for
   * @param {function(google.maps.MapMouseEvent): void} callback function to call when event is triggered
   */
  addMapListener: function(listenerID, eventName, callback) {
    if (_listeners.has(listenerID)) {
      console.warn(`MapService.addMapListener: Listener with ID ${listenerID} already exists. Removing old listener.`);
      this.removeMapListener(listenerID);
    }
    const listener = this.getMap().addListener(eventName, callback);
    _listeners.set(listenerID, listener);
  },
  /**
   * @param {string} listenerID
   * @return {boolean} true if listener exists, false if not
   */
  hasListener: function(listenerID) {
    return _listeners.has(listenerID);
  },
  /**
   * Returns the radius between the North-East and South-West corners of the visible portion of the map. Returns a
   * radius of 1 if the map is zoomed out farther than the zoom threshold, regardless of the actual radius and overscan.
   * overscanInMeters is provided to compensate for large features that should show even when the center is outside the
   * visible area.
   * @param {number} zoomThreshold The zoom level at which to begin showing overlays.
   * @param {number} overscanInMeters The number of meters by which to overscan the visible area.
   * @return {number} The radius in kilometers.
   */
  getMapRadius: function(zoomThreshold = 13, overscanInMeters = 4500) {
    if (this.getMap().getZoom() < zoomThreshold) return 1;

    const bounds = this.getMap().getBounds();
    const boundNE = [bounds.getNorthEast().lat(), bounds.getNorthEast().lng()];
    const boundSW = [bounds.getSouthWest().lat(), bounds.getSouthWest().lng()];
    return ((distanceBetween(boundNE, boundSW) / 2) * 1000) + (overscanInMeters / 1000);
  },
  /**
   * Remove a single listener from the map
   * @param {string} listenerID name of event to remove
   * @return {boolean} true if event was removed, false if not removed, or did not exist
   */
  removeMapListener: function(listenerID) {
    const listener = _listeners.get(listenerID);
    if (listener) {
      gmAPI.event.removeListener(listener);
      return _listeners.delete(listenerID);
    } else {
      return false;
    }
  },
  /**
   * Creates an array of coordinates for a circle around the given marker's lat and lng.
   * @param {number} centerLat - Latitude of marker
   * @param {number} centerLng - Longitude of marker
   * @param {number} numberOfPoints - Number of comps attached to a marker
   * @return {Array<LatLngLiteral>} Array of coordinates `[{lat: Number, lng: Number})]`
   */
  createCircle: function(centerLat, centerLng, numberOfPoints) {
    const points = [];
    const pi = Math.PI;
    const radius = 20;

    const EARTH_RADIUS = 6378100.0;
    // Convert to radians.
    const lat = (centerLat * pi) / 180.0;
    const lon = (centerLng * pi) / 180.0;

    for (let t = 0; t < numberOfPoints; t += 1) {
      // y
      const latPoint = lat + (radius / EARTH_RADIUS) * Math.sin(t);
      // x
      const lonPoint =
            lon + ((radius / EARTH_RADIUS) * Math.cos(t)) / Math.cos(lat);
      const point = {
        lat: (latPoint * 180.0) / pi,
        lng: (lonPoint * 180.0) / pi,
      };
      points.push(point);
    }

    return points;
  },
  /**
   * Set the map options
   * @param {google.maps.MapOptions} options map options
   */
  setOptions: function(options) {
    this.getMap().setOptions(options);
  },
  /**
   * Centers and optionally zooms the viewport on a position. Uses 'panTo' instead of setOptions to provide
   * the 'drag' animation behavior
   * @param {google.maps.LatLng | google.maps.LatLngLiteral} position The position to center on.
   * @param {number} [zoom] The level to zoom to. If not supplied, the viewport's current zoom level is retained.
   * @return {Promise<void>} A promise that resolves when the map has centered on the position.
   */
  centerViewport: function(position, zoom) {
    if (!position) {
      console.warn('centerViewport: required argument `position` was not supplied; aborting.');
      return;
    }
    return new Promise((resolve) => {
      this.addMapListener('centerViewport_idle', 'idle', () => {
        MapService.removeMapListener('centerViewport_idle');
        resolve();
      });
      if (zoom !== undefined) {
        this.getMap().setZoom(zoom);
      }
      this.getMap().panTo(position);
    });
  },
  /**
   * Get the map object
   * @return {google.maps.Map} map object
   */
  getMap: function() {
    if (!_map) {
      throw new Error('Map not initialized');
    }
    return _map;
  },

  /**
   * Indicates whether the map has been initialized.
   * @return {boolean}
   */
  isMapInitialized: function() {
    return !!_map;
  },

  /**
   * Set the cursor style mode on the map, or call with null/no arguments to unset the current style
   * @param {CursorOption} cursor true to enable crosshair style mode, false to disable
   */
  setCursor: function(cursor) {
    if (!_mapElement) {
      throw new Error('Map not initialized');
    }

    _mapElement.className = '';
    // draggableCursor and draggingCursor are reliant on mouseover events, so the cursor doesn't change until the
    // mouse moves. But in some cases we want the cursor to change immediately, so we change the style directly.
    const gmStyleDiv = _mapElement.getElementsByClassName('gm-style')[0];

    if (cursor && typeof cursor === 'string') {
      _mapElement.classList.add(cursor);
      switch (cursor) {
        case 'wait':
          if (gmStyleDiv?.firstChild instanceof HTMLElement) {
            gmStyleDiv.firstChild.style.cursor = 'wait';
          }
          this.setOptions({draggableCursor: 'wait'});
          break;
        case 'crosshair':
          this.setOptions({draggableCursor: 'crosshair'});
          break;
        case 'pencil':
          this.setOptions({
            draggableCursor: 'url(' + pencilIcon + ') 0 25, grab',
            draggingCursor: 'url(' + pencilIcon + ') 0 25, grabbing',
          });
          break;
        case 'text':
          this.setOptions({draggableCursor: 'text'});
          break;
        case 'pointer':
          this.setOptions({draggableCursor: 'pointer'});
          break;
        case 'move':
          this.setOptions({draggableCursor: 'move', draggingCursor: 'move'});
          break;
        default:
          if (gmStyleDiv?.firstChild instanceof HTMLElement) {
            gmStyleDiv.firstChild.style.cursor = 'grab';
          }
          this.setOptions({draggableCursor: 'grab', draggingCursor: 'grabbing'});
          break;
      }
      return;
    }
    this.setOptions({
      draggableCursor: 'grab',
      draggingCursor: 'grabbing',
    });
  },
  // Calculates tile coordinates for the current map position
  getTileCoords() {
    const scale = 1 << _map.getZoom();
    let siny = Math.sin((_map.getCenter().lat() * Math.PI) / 180);
    siny = Math.min(Math.max(siny, -0.9999), 0.9999);

    const worldCoord = new gmAPI.Point(
      256 * (0.5 + _map.getCenter().lng() / 360),
      256 * (0.5 - Math.log((1 + siny) / (1 - siny)) / (4 * Math.PI)),
    );
    return new gmAPI.Point(
      Math.floor((worldCoord.x * scale) / 256),
      Math.floor((worldCoord.y * scale) / 256),
    );
  },
  /**
   * Retrieves the Beyond Maps sub-panes from the Map.
   * @return {Promise<HTMLDivElement[]>} A Promise resolving to an array of the sub-pane elements.
   */
  getSubPanes() {
    if (subPanes === null) {
      subPanes = createSubPanes(this.getMap());
    }
    return subPanes;
  },

  /**
   * Retrieves a `MapCanvasProjection` from the Map.
   * @return {Promise<google.maps.MapCanvasProjection>}
   */
  getCanvasProjection() {
    return fetchCanvasProjection(this.getMap());
  },

  async getWorldWidth() {
    const projection = await this.getCanvasProjection();
    return projection.getWorldWidth();
  },

  /**
   * Caches the visible region of the map for a single frame to prevent multiple calls to getVisibleRegion. In order to
   * calculate the visible region, the layout must be updated, so calls to getVisibleRegion must be minimized. This
   * function should be used in place of getVisibleRegion for any code that may be called multiple times per frame.
   *
   * The cached value is cleared at the start of the next frame, forcing a new layout, but it should be limited to
   * once per frame.
   * @param {google.maps.MapCanvasProjection} projection The projection made available in the OverlayView's draw method.
   * @return {google.maps.LatLngBounds}
   */
  getViewBounds(projection) {
    if (!viewBounds) {
      viewBounds = projection.getVisibleRegion().latLngBounds;
      requestAnimationFrame(() => viewBounds = null);
    }
    return viewBounds;
  },
  /**
   * Accepts a query string and checks to see if it can extract a valid latitude and longitude.
   * If the query supplies a valid zoom level that will be used, otherwise it will default to zoom 12.
   * @param {string} query - A query string containing params 'lat', 'lng', and optionally 'zoom'
   * @return {Partial<MapSettings>} - function returns a MapSettings object
   * returns an object with the location components if the query is valid, or an empty object otherwise.
   */
  mapSettingsFromQuery(query) {
    const params = new URLSearchParams(query);
    if (!params.has('lat') || !params.has('lng')) {
      // If the query is missing either latitude or longitude, then there is no way to define a valid location
      return {};
    }
    const lat = parseFloat(params.get('lat'));
    if (isNaN(lat) || lat > 90 || lat < -90) {
      return {};
    }
    const lng = parseFloat(params.get('lng'));
    if (isNaN(lng) || lng > 180 || lng < -180) {
      return {};
    }
    const loc = {
      zoom: 12,
      lat: lat,
      lng: lng,
    };
    if (params.has('zoom')) {
      const z = parseInt(params.get('zoom'));
      if (!isNaN(z) && z >= 0 && z <= 21) {
        loc.zoom = z;
      }
    }
    return loc;
  },

  /**
   * Converts the map settings to a query string and returns it.
   * @param {string} mapSettings URL param string with three parameters: 'zoom', 'lat', and 'lng'.
   * @return {URLSearchParams} URLSearchParams object, which represents a query string.
   */
  mapSettingsToQuery(mapSettings) {
    const map = this.getMap();
    if (!map) {
      return new URLSearchParams();
    }
    if (!map.getCenter) {
      return new URLSearchParams(mapSettings);
    }
    const queryString = new URLSearchParams(mapSettings);
    queryString.set('zoom', map.getZoom().toString());
    queryString.set('lat', map.getCenter().lat().toString());
    queryString.set('lng', map.getCenter().lng().toString());

    return queryString;
  },
  /**
   * Finds the map tile element from the given screen coordinates.
   * @param {Point} point - The coordinates of the point on the screen.
   * @param {(elt: HTMLImageElement) => boolean} imgMatcher - Function for find() to match a specific tile.
   * @return {HTMLImageElement|null} - The matched tile element, or null if not found.
   * @throws {Error} - Throws an error if x and y are not numbers, or if imgMatcher is not a function.
   */
  tileFromPoint({x, y} = {x: null, y: null}, imgMatcher) {
    // validate that x and y are numbers and srcMatcher is a function
    if (typeof x !== 'number' || typeof y !== 'number' || typeof imgMatcher !== 'function') {
      throw new Error('tileFromPoint: x and y must be numbers, and srcMatcher must be a function');
    }
    /** @type {HTMLImageElement} */
    // @ts-ignore filter() will return only HTMLImageElement-like elements
    const tile = document
      .elementsFromPoint(x, y)
      .filter((element) => element.tagName === 'IMG')
      .find(imgMatcher);

    return tile ?? null;
  },
  /**
   * Finds the map tile elements in a given radius from the clicked point.
   *
   * Does not require the Map SDK per se, but assumes a regular rectangular grid of image elements, e.g. a map layer.
   * @param {Point} point - The coordinates of the point on the screen.
   * @param {number} radius - The radius in pixels to search for tiles.
   * @param {(elt: Element) => boolean} imgMatcher - Function for find() to match a specific tile.
   * @return {TileResult[]} - The matched tile elements, or an empty array if not found.
   * @throws {Error} - Throws an error if x, y, and radius are not numbers, or if srcMatcher is not a function.
   */
  tilesFromPoint({x, y} = {x: null, y: null}, radius = 7, imgMatcher) {
    // validate that x, y and radius are numbers and srcMatcher is a function
    if (
      typeof x !== 'number' ||
      typeof y !== 'number' ||
      typeof radius !== 'number' ||
      typeof imgMatcher !== 'function'
    ) {
      throw new Error('tilesFromPoint: x, y, and radius must be numbers, and srcMatcher must be a function');
    }

    // get the initial tile at the clicked point
    const tile = this.tileFromPoint({x, y}, imgMatcher);

    if (!tile) return [];

    // the bounding client rect is correct as it takes into account all parent transforms, where the img's x and y
    // properties do not
    const rect = tile.getBoundingClientRect();

    // calculate clicked pixel on image from window click coordinates and image offset
    const imgX = x - rect.left;
    const imgY = y - rect.top;

    // We need to search for tiles within $RADIUS pixels of the clicked point, which could result in searching
    // up to 4 tiles if the clicked point is near the edge of a tile. Find all the tiles that need to be searched.
    // Tiles that need to be checked:
    // A | B | C
    // D | * | F
    // G | H | I
    const tilePoints = [];

    if (imgX < radius) {
      tilePoints.push({x: x - radius, y: y, placement: 'D'});
      if (imgY < radius) {
        tilePoints.push({x: x - radius, y: y - radius, placement: 'A'});
      }
    }
    if (imgY < radius) {
      tilePoints.push({x: x, y: y - radius, placement: 'B'});
      if (imgX > tile.width - radius) {
        tilePoints.push({x: x + radius, y: y - radius, placement: 'C'});
      }
    }
    if (imgX > tile.width - radius) {
      tilePoints.push({x: x + radius, y: y, placement: 'F'});
      if (imgY > tile.height - radius) {
        tilePoints.push({x: x + radius, y: y + radius, placement: 'I'});
      }
    }
    if (imgY > tile.height - radius) {
      tilePoints.push({x: x, y: y + radius, placement: 'H'});
      if (x < radius) {
        tilePoints.push({x: x - radius, y: y + radius, placement: 'G'});
      }
    }

    const tiles = tilePoints.map((point) => {
      return {
        el: this.tileFromPoint({x: point.x, y: point.y}, imgMatcher),
        placement: point.placement,
      };
    }).filter((tile) => tile.el);

    tiles.unshift({el: tile, placement: 'E'});

    return tiles;
  },

  /**
   * @param {number} feet
   * @param {google.maps.LatLngBounds} [mapBounds]
   * @return {number}
   */
  feetToHorizontalPixels(feet, mapBounds) {
    const map = this.getMap();
    if (!mapBounds) {
      mapBounds = map.getBounds();
      if (!mapBounds) {
        throw new Error('Map bounds are required to convert feet to pixels.');
      }
    }
    const worldWidth = horizontalMapDistance(mapBounds);
    const screenWidth = map.getDiv().offsetWidth;
    return feet * screenWidth / worldWidth;
  },

  /**
   * @param {number} feet
   * @param {google.maps.LatLngBounds} [mapBounds]
   * @return {number}
   */
  feetToVerticalPixels(feet, mapBounds) {
    const map = MapService.getMap();
    if (!mapBounds) {
      mapBounds = map.getBounds();
      if (!mapBounds) {
        throw new Error('Map bounds are required to convert feet to pixels.');
      }
    }
    const worldHeight = verticalMapDistance(mapBounds);
    const screenHeight = map.getDiv().offsetHeight;
    return feet * screenHeight / worldHeight;
  },

  /**
   * @param {number} xPos The X coordinate
   * @param {google.maps.LatLngBounds} [mapBounds]
   * @return {number}
   */
  horizontalPixelsToFeet(xPos, mapBounds) {
    const map = MapService.getMap();
    if (!mapBounds) {
      mapBounds = map.getBounds();
      if (!mapBounds) {
        throw new Error('Map bounds are required to convert feet to pixels.');
      }
    }
    const worldWidth = horizontalMapDistance(mapBounds);
    const screenWidth = map.getDiv().offsetWidth;
    return xPos * worldWidth / screenWidth;
  },

  /**
   * @param {number} yPos The Y coordinate
   * @param {google.maps.LatLngBounds} [mapBounds]
   * @return {number}
   */
  verticalPixelsToFeet(yPos, mapBounds) {
    const map = MapService.getMap();
    if (!mapBounds) {
      mapBounds = map.getBounds();
      if (!mapBounds) {
        throw new Error('Map bounds are required to convert feet to pixels.');
      }
    }
    const worldHeight = verticalMapDistance(mapBounds);
    const screenHeight = map.getDiv().offsetHeight;
    return yPos * worldHeight / screenHeight;
  },

  /**
   * This function is based on `fromLatLngToPoint` from the vector-tiles-google-maps package.
   * @param {CustomClickEvent} event The click event to convert
   * @return {Point} The screen pixel coordinates of the latitude and longitude.
   */
  fromClickEventToPoint(event, tileSize = 256) {
    const map = this.getMap();
    const tile = mercator.getTileAtLatLng(event.latLng, Math.round(map.getZoom()), tileSize);
    const tileBounds = mercator.getTileBounds(tile, tileSize);
    const tileSwLatLng = new gmAPI.LatLng(tileBounds.sw);
    const tileNeLatLng = new gmAPI.LatLng(tileBounds.ne);
    const tileSwPixels = mercator.fromLatLngToPixels(map, tileSwLatLng);
    const tileNePixels = mercator.fromLatLngToPixels(map, tileNeLatLng);
    // Scale the pixel coordinates to the tile's size because the bounds may be larger or smaller than the tile's size
    // when fractional zooming is enabled
    const scaleX = tileSize / (tileNePixels.x - tileSwPixels.x);
    const scaleY = tileSize / (tileNePixels.y - tileSwPixels.y);
    const scaledX = (event.pixel.x - tileSwPixels.x) * scaleX;
    const scaledY = (event.pixel.y - tileNePixels.y) * scaleY;
    return {
      x: Math.abs(scaledX),
      y: Math.abs(scaledY),
    };
  },
  /**
   * Calculates the center of an object from its map bounds.
   * @param {google.maps.LatLngBoundsLiteral|google.maps.LatLngBounds|LatLngBoundsLiteralStrings} bounds
   * The object's map bounds.
   * @return {google.maps.LatLng}
   */
  calculateCenter(bounds) {
    if (bounds instanceof gmAPI.LatLngBounds) {
      return bounds.getCenter();
    }

    const n = typeof bounds.north === 'string' ? parseFloat(bounds.north) : bounds.north;
    const s = typeof bounds.south === 'string' ? parseFloat(bounds.south) : bounds.south;
    const e = typeof bounds.east === 'string' ? parseFloat(bounds.east) : bounds.east;
    const w = typeof bounds.west === 'string' ? parseFloat(bounds.west) : bounds.west;

    return new gmAPI.LatLng((n + s) / 2, (e + w) / 2);
  },
};

// Private functions
/**
 * Retrieves the panes from a Map.
 * @private
 * @param {google.maps.Map} map The map to fetch panes from.
 * @return {Promise<google.maps.MapPanes>} A Promise resolving to a Record of named panes.
 */
function fetchMapPanes(map) {
  return new Promise((resolve, reject) => {
    // The Google Maps API does not implement `getPanes` directly on Map.
    // As a workaround, we create a dummy OverlayView, attach it to the Map, and call its `getPanes`.
    const overlayView = new gmAPI.OverlayView();
    const timeout = setTimeout(() => {
      reject(new Error(`fetchMapPanes: OverlayView.onAdd wasn't called within 5s. This indicates an issue with the Map or OverlayView.`));
    }, 5000);
    overlayView.onAdd = () => {
      clearTimeout(timeout);
      resolve(overlayView.getPanes());
      overlayView.setMap(null);
    };
    overlayView.onRemove = () => {}; // Dummy implementation required to avoid Maps error
    overlayView.setMap(map);
  });
}

/**
 * Fetches a `MapCanvasProjection` from a Map.
 * @param {google.maps.Map} map
 * @return {Promise<google.maps.MapCanvasProjection>}
 */
function fetchCanvasProjection(map) {
  return new Promise((resolve, reject) => {
    const overlayView = new gmAPI.OverlayView();
    const timeout = setTimeout(() => {
      reject(new Error(`fetchCanvasProjection: OverlayView.onAdd wasn't called within 5s. This indicates an issue with the Map or OverlayView.`));
    }, 5000);
    overlayView.onAdd = () => {
      clearTimeout(timeout);
      resolve(overlayView.getProjection());
      overlayView.setMap(null);
    };
    overlayView.onRemove = () => {}; // Dummy implementation required to avoid Maps error
    overlayView.setMap(map);
  });
}

/**
 * Creates the Map sub-panes for Beyond Maps Graphics and populates them into `subpanes`.
 * @private
 * @param {google.maps.Map} map The map to add sub-panes to.
 * @param {string[]} subPaneIds The IDs of the sub-panes to create.
 * @return {Promise<HTMLDivElement[]>} A Promise resolving to the array of created sub-panes.
 */
async function createSubPanes(map, subPaneIds = SUBPANE_IDS) {
  const {overlayLayer} = await fetchMapPanes(map);
  return subPaneIds.map((name) => {
    const element = document.createElement('div');
    element.id = name;
    // Position below Maps API elements (e.g., Polyline).
    element.style.position = 'relative';
    element.style.zIndex = '1';
    overlayLayer.appendChild(element);
    return element;
  });
}

/**
 * @param {google.maps.LatLngBounds} bounds
 * @return {number}
 */
const horizontalMapDistance = (bounds) => {
  const lat1 = bounds.getNorthEast().lat();
  const lat2 = bounds.getNorthEast().lat();
  const lng1 = bounds.getNorthEast().lng();
  const lng2 = bounds.getSouthWest().lng();
  return haversine(lat1, lng1, lat2, lng2);
};

/**
 * @param {google.maps.LatLngBounds} bounds
 * @return {number}
 */
const verticalMapDistance = (bounds) => {
  const lat1 = bounds.getNorthEast().lat();
  const lat2 = bounds.getSouthWest().lat();
  const lng1 = bounds.getNorthEast().lng();
  const lng2 = bounds.getNorthEast().lng();
  return haversine(lat1, lng1, lat2, lng2);
};

/**
 * @param {number} lat1
 * @param {number} lng1
 * @param {number} lat2
 * @param {number} lng2
 * @return {number}
 */
const haversine = (lat1, lng1, lat2, lng2) => {
  const dLat = lat2 * Math.PI / 180 - lat1 * Math.PI / 180;
  const dLng = lng2 * Math.PI / 180 - lng1 * Math.PI / 180;

  const a =
  Math.sin(dLat / 2) * Math.sin(dLat / 2) + Math.cos(lat1 * Math.PI / 180) *
  Math.cos(lat2 * Math.PI / 180) * Math.sin(dLng / 2) * Math.sin(dLng / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const d = EARTH_RADIUS_KM * c;
  return d * KM_TO_FEET;
};

export {
  MapService,
  gmAPI,

  // export for testing
  _map,
  _mapElement,
  _listeners,
};
